import { useMemo, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { MbscEventcalendarView } from "@mobiscroll/react";

import { useReservationsState, ReservationActions } from "../../../../store/reservations.reducer";
import { Reservation } from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    calculateEarliestReservationByDateRange,
    calculateEndDateWithTimelineSpan,
    daySlices,
    getTimelineSpanDates,
} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";

export const useCalendarView = (
    appId: string,
    reservations: Reservation[],
    startDateFinal: Date
) => {
    const dispatch = useDispatch();
    const hasDispatchedRef = useRef(false);
    const prevReservationsLengthRef = useRef(reservations.length);

    const {
        reservationDaySlice,
        viewMode,
        timelineStep,
        timelineSpan,
        startTimeFrame,
        endTimeFrame,
    } = useReservationsState(appId);

    // Handle date range updates separately to avoid infinite loop
    useEffect(() => {
        // Only dispatch if reservations length changed from 0 to > 0, or if we haven't dispatched yet
        const shouldDispatch =
            reservations.length > 0 &&
            (prevReservationsLengthRef.current === 0 || !hasDispatchedRef.current);

        if (shouldDispatch) {
            const [startDate, endDate] = getTimelineSpanDates(timelineSpan, startDateFinal);

            const earliestReservationDateInPeriod = calculateEarliestReservationByDateRange(
                startDate,
                endDate,
                reservations,
                true
            );

            if (earliestReservationDateInPeriod) {
                const endTime = calculateEndDateWithTimelineSpan(
                    earliestReservationDateInPeriod as Date,
                    timelineSpan
                );

                dispatch({
                    type: ReservationActions.SET_RESERVATIONS,
                    appId: appId,
                    payload: {
                        fromDate: earliestReservationDateInPeriod?.toISOString(),
                        toDate: endTime?.toISOString()
                    }
                });

                hasDispatchedRef.current = true;
            }
        }

        prevReservationsLengthRef.current = reservations.length;
    }, [timelineSpan, reservations.length, appId]);

    // Reset dispatch flag when timelineSpan changes
    useEffect(() => {
        hasDispatchedRef.current = false;
    }, [timelineSpan]);

    const calView = useMemo<MbscEventcalendarView>(() => {
        let timelineStart: string = '00:00';
        let timelineEnd: string = '23:59';

        const allowed: 'day' | 'morning' | 'evening' | 'custom' | undefined =
            reservationDaySlice === 'day' ||
            reservationDaySlice === 'morning' ||
            reservationDaySlice === 'evening' ||
            reservationDaySlice === 'custom'
                ? reservationDaySlice
                : undefined;

        if (reservationDaySlice === 'custom') {
            timelineStart = startTimeFrame || '00:00';
            timelineEnd = endTimeFrame || '23:59';
        } else {
            timelineStart = daySlices(allowed, timelineSpan)[0];
            timelineEnd = daySlices(allowed, timelineSpan)[1];
        }

        return viewMode === 'timeGrid' ? {
            schedule: {
                type: timelineSpan,
                timeCellStep: timelineStep,
                timeLabelStep: timelineStep,
                startTime: timelineStart,
                endTime: timelineEnd,
                allDay: false,
            }
        } : {
            timeline: {
                type: timelineSpan,
                timeCellStep: timelineStep,
                timeLabelStep: timelineStep,
                startTime: timelineStart,
                endTime: timelineEnd,
                rowHeight: 'equal'
            }
        };
    }, [
        reservationDaySlice,
        timelineSpan,
        viewMode,
        timelineStep,
        startTimeFrame,
        endTimeFrame,
    ]);

    return calView;
};
